const POSTHOG_SCRIPT_ID = 'posthog-js'
const ATTRIBUTION_STORAGE_KEY = 'prufa.analytics.attribution.v1'
const LANDING_DISTINCT_ID_KEY = 'prufa.analytics.landing_distinct_id.v1'

let initialized = false
let scriptLoadingPromise = null
let pendingUser = null
let identifiedEmail = null
let identifiedWorkspaceId = null

function getConfig() {
  const key = import.meta.env.VITE_POSTHOG_KEY
  return { key }
}

function sanitizeText(value) {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function pickParam(params, key) {
  return sanitizeText(params.get(key))
}

function readJsonStorage(key) {
  try {
    const raw = window.sessionStorage.getItem(key)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return typeof parsed === 'object' && parsed ? parsed : {}
  } catch {
    return {}
  }
}

function writeJsonStorage(key, value) {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value))
  } catch {
    // ignore storage failures
  }
}

function extractDomain(value) {
  const normalized = sanitizeText(value)
  if (!normalized) return undefined
  try {
    return sanitizeText(new URL(normalized).hostname)
  } catch {
    const withoutProtocol = normalized.replace(/^[a-z]+:\/\//i, '')
    const hostCandidate = withoutProtocol.split(/[/?#]/, 1)[0]?.trim()
    return sanitizeText(hostCandidate?.replace(/:\d+$/, ''))
  }
}

export function bootstrapAttributionFromUrl(path = window.location.pathname) {
  const params = new URLSearchParams(window.location.search)
  const existing = readJsonStorage(ATTRIBUTION_STORAGE_KEY)
  const referrer = pickParam(params, 'referrer') || existing.referrer || sanitizeText(document.referrer)
  const referringDomain = pickParam(params, 'referring_domain') || extractDomain(referrer) || existing.referring_domain
  const landingDistinctId = pickParam(params, 'ph_distinct_id')

  if (landingDistinctId) {
    try {
      window.sessionStorage.setItem(LANDING_DISTINCT_ID_KEY, landingDistinctId)
    } catch {
      // ignore storage failures
    }
  }

  const context = {
    ...existing,
    source: pickParam(params, 'src') || existing.source,
    intent: pickParam(params, 'intent') || existing.intent,
    entry_path: pickParam(params, 'entry_path') || existing.entry_path,
    entry_url: pickParam(params, 'entry_url') || existing.entry_url,
    referrer,
    referring_domain: referringDomain,
    utm_source: pickParam(params, 'utm_source') || existing.utm_source,
    utm_medium: pickParam(params, 'utm_medium') || existing.utm_medium,
    utm_campaign: pickParam(params, 'utm_campaign') || existing.utm_campaign,
    utm_term: pickParam(params, 'utm_term') || existing.utm_term,
    utm_content: pickParam(params, 'utm_content') || existing.utm_content,
  }

  writeJsonStorage(ATTRIBUTION_STORAGE_KEY, context)
  return {
    ...context,
    current_path: path,
    current_url: window.location.origin + path,
    app_host: window.location.host,
  }
}

function loadScript() {
  if (scriptLoadingPromise) return scriptLoadingPromise

  scriptLoadingPromise = new Promise((resolve, reject) => {
    if (getPosthog()) {
      resolve()
      return
    }
    if (document.getElementById(POSTHOG_SCRIPT_ID)) {
      resolve()
      return
    }

    const script = document.createElement('script')
    script.id = POSTHOG_SCRIPT_ID
    script.async = true
    script.crossOrigin = 'anonymous'
    script.src = `${window.location.origin}/rly/static/array.js`
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load PostHog script'))
    document.head.appendChild(script)
  })

  return scriptLoadingPromise
}

function getPosthog() {
  return window.posthog || null
}

function buildEventProps(extra = {}) {
  return {
    ...bootstrapAttributionFromUrl(),
    ...extra,
  }
}

function syncIdentity() {
  const posthog = getPosthog()
  if (!posthog || !initialized) return

  if (!pendingUser) {
    if (identifiedEmail) {
      posthog.reset()
      identifiedEmail = null
      identifiedWorkspaceId = null
    }
    return
  }

  if (
    identifiedEmail === pendingUser.email
    && identifiedWorkspaceId === pendingUser.workspace_id
  ) {
    return
  }

  try {
    const landingDistinctId = window.sessionStorage.getItem(LANDING_DISTINCT_ID_KEY)
    if (landingDistinctId) {
      posthog.alias(landingDistinctId)
      window.sessionStorage.removeItem(LANDING_DISTINCT_ID_KEY)
    }
  } catch {
    // alias can fail if identities have already been merged
  }

  posthog.identify(pendingUser.email, {
    email: pendingUser.email,
    workspace_id: pendingUser.workspace_id,
    display_name: pendingUser.display_name || undefined,
    tier: pendingUser.tier || undefined,
    is_paid: Boolean(pendingUser.is_paid),
  })

  identifiedEmail = pendingUser.email
  identifiedWorkspaceId = pendingUser.workspace_id
}

export async function initAnalytics(consented = null) {
  const { key } = getConfig()
  if (!key || typeof window === 'undefined') return

  await loadScript()
  const posthog = getPosthog()
  if (!posthog) return

  if (!initialized) {
    posthog.init(key, {
      api_host: `${window.location.origin}/rly`,
      ui_host: 'https://us.posthog.com',
      capture_pageview: false,
      persistence: consented === true ? 'localStorage+cookie' : 'memory',
      person_profiles: 'identified_only',
      opt_out_capturing_by_default: consented === false,
      cookieless_mode: consented === false ? 'on_reject' : 'off',
      // Mask every form input in session replay so no typed PII (emails,
      // passwords, the URL someone audits) is ever captured on the wire.
      // This is what lets us record anonymous (un-consented) sessions without
      // capturing identity — see the recording-start logic below.
      session_recording: { maskAllInputs: true },
    })
    // product isolates Prufa events in the shared PostHog project (255476, also
    // openclaw/lobsterland); surface separates the app from the landing.
    // Registered as super-properties so EVERY event — autocapture, $pageview,
    // recordings, $identify — carries them, not just the manual capture() calls.
    // The backend stamps product='prufa' too (service/analytics.py); this makes
    // the front-end funnel filterable by the same key.
    posthog.register({ product: 'prufa', surface: 'app' })
    initialized = true
  }

  if (consented === false) {
    posthog.set_config({ persistence: 'memory' })
    posthog.opt_out_capturing()
    posthog.stopSessionRecording()
    return
  }

  posthog.set_config({ persistence: consented === true ? 'localStorage+cookie' : 'memory' })
  posthog.opt_in_capturing()
  // Record consented AND undecided (anonymous) sessions. Undecided sessions use
  // memory persistence (no durable cookie) and get no person profile
  // (person_profiles: 'identified_only'), and all inputs are masked — so the
  // replay carries behaviour without identity or typed PII. Only an EXPLICIT
  // Decline (consented === false, handled above) stops recording.
  // NOTE: in the EU, ePrivacy may still require opt-in for replay even when
  // masked. Geo-gate or revert to consented-only if you target EU traffic.
  posthog.startSessionRecording()
  syncIdentity()
}

export function normalizeAnalyticsPath(path) {
  if (path === '/dashboard' || path === '/dashboard/') return '/'
  if (path.startsWith('/dashboard/')) return path.slice('/dashboard'.length)
  return path
}

export function trackPageview(path) {
  const posthog = getPosthog()
  if (!posthog) return
  posthog.capture('$pageview', buildEventProps({
    $current_url: window.location.origin + path,
  }))
}

export function trackEvent(event, props = {}) {
  const posthog = getPosthog()
  if (!posthog) return
  posthog.capture(event, buildEventProps(props))
}

export function setAnalyticsUser(user) {
  if (!user?.workspace_id || !user?.email) {
    pendingUser = null
    syncIdentity()
    return
  }
  pendingUser = {
    workspace_id: user.workspace_id,
    email: user.email,
    display_name: user.display_name || '',
    tier: user.tier || '',
    is_paid: Boolean(user.is_paid),
  }
  syncIdentity()
}

export function trackCtaClick(ctaName, ctaLocation, targetUrl) {
  const posthog = getPosthog()
  if (!posthog) return
  posthog.capture('cta_clicked', buildEventProps({
    cta_name: ctaName,
    cta_location: ctaLocation,
    target_url: targetUrl,
  }))
}

export function trackSignupStarted(signupMethod = 'email') {
  const posthog = getPosthog()
  if (!posthog) return
  posthog.capture('signup_started', buildEventProps({
    signup_method: signupMethod,
  }))
}

export function trackPaywallViewed(paywallReason = 'manual_visit') {
  const posthog = getPosthog()
  if (!posthog) return
  posthog.capture('paywall_viewed', buildEventProps({
    tier_at_view: pendingUser?.tier || 'free',
    paywall_reason: paywallReason,
  }))
}

export function trackCheckoutStarted(tier, isTrialOffered = false) {
  const posthog = getPosthog()
  if (!posthog) return
  posthog.capture('checkout_started', buildEventProps({
    tier,
    is_trial_offered: isTrialOffered,
  }))
}
